/**
 * WebSocket Service for Real-time Messaging
 * 
 * Manages WebSocket connections, message delivery, typing indicators,
 * and presence updates for the messaging system.
 * 
 * Requirements: 6.2, 6.3, 6.4, 6.5, 4.5
 */

import { getIdToken } from './authService';

class WebSocketService {
  constructor() {
    this.ws = null;
    this.connectionId = null;
    this.isConnecting = false;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Start with 1 second
    this.maxReconnectDelay = 30000; // Max 30 seconds
    this.reconnectTimer = null;
    this.heartbeatTimer = null;
    this.heartbeatInterval = 30000; // 30 seconds
    
    // Event listeners
    this.messageListeners = [];
    this.statusListeners = [];
    this.typingListeners = [];
    this.presenceListeners = [];
    this.connectionListeners = [];
    this.errorListeners = [];
    
    // Pending messages queue (for when disconnected)
    this.pendingMessages = [];
    
    // User ID (set on connect)
    this.userId = null;
  }

  /**
   * Get WebSocket URL based on environment
   * @returns {string} WebSocket URL
   */
  getWebSocketUrl() {
    // Import config function
    const { getWebSocketUrl } = require('../config/config');
    return getWebSocketUrl();
  }

  /**
   * Connect to WebSocket server
   * @param {string} userId - User ID for authentication
   * @returns {Promise<void>}
   */
  async connect(userId) {
    if (this.isConnected || this.isConnecting) {
      console.log('WebSocket already connected or connecting');
      return;
    }

    this.isConnecting = true;
    this.userId = userId;

    try {
      // Get authentication token
      const token = await getIdToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      // Build WebSocket URL with auth parameters
      const wsUrl = this.getWebSocketUrl();
      const urlWithAuth = `${wsUrl}?userId=${encodeURIComponent(userId)}`;

      console.log('Connecting to WebSocket:', wsUrl);

      // Create WebSocket connection
      this.ws = new WebSocket(urlWithAuth);

      // Set up event handlers
      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
      this.ws.onerror = this.handleError.bind(this);
      this.ws.onclose = this.handleClose.bind(this);

    } catch (error) {
      console.error('Error connecting to WebSocket:', error);
      this.isConnecting = false;
      this.notifyError(error);
      this.scheduleReconnect();
    }
  }

  /**
   * Handle WebSocket connection opened
   */
  handleOpen(event) {
    console.log('WebSocket connected');
    this.isConnected = true;
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    this.reconnectDelay = 1000;

    // Notify connection listeners
    this.notifyConnectionChange(true);

    // Start heartbeat
    this.startHeartbeat();

    // Send any pending messages
    this.sendPendingMessages();
  }

  /**
   * Handle incoming WebSocket messages
   */
  handleMessage(event) {
    try {
      const data = JSON.parse(event.data);
      console.log('WebSocket message received:', data);

      // Route message based on type
      switch (data.type) {
        case 'new_message':
          this.notifyMessageListeners(data.message);
          break;
        
        case 'typing_indicator':
          this.notifyTypingListeners(data);
          break;
        
        case 'presence_update':
          this.notifyPresenceListeners(data);
          break;
        
        case 'status_update':
          this.notifyStatusListeners(data);
          break;
        
        default:
          console.log('Unknown message type:', data.type);
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  }

  /**
   * Handle WebSocket errors
   */
  handleError(event) {
    console.error('WebSocket error:', event);
    this.notifyError(new Error('WebSocket connection error'));
  }

  /**
   * Handle WebSocket connection closed
   */
  handleClose(event) {
    console.log('WebSocket closed:', event.code, event.reason);
    this.isConnected = false;
    this.isConnecting = false;

    // Stop heartbeat
    this.stopHeartbeat();

    // Notify connection listeners
    this.notifyConnectionChange(false);

    // Attempt to reconnect if not a normal closure
    if (event.code !== 1000) {
      this.scheduleReconnect();
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    console.log('Disconnecting WebSocket');
    
    // Clear reconnect timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Stop heartbeat
    this.stopHeartbeat();

    // Close WebSocket connection
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }

    this.isConnected = false;
    this.isConnecting = false;
    this.reconnectAttempts = 0;
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      this.notifyError(new Error('Failed to reconnect to WebSocket'));
      return;
    }

    // Clear existing timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    // Calculate delay with exponential backoff
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts),
      this.maxReconnectDelay
    );

    console.log(`Scheduling reconnect in ${delay}ms (attempt ${this.reconnectAttempts + 1})`);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      if (this.userId) {
        this.connect(this.userId);
      }
    }, delay);
  }

  /**
   * Start heartbeat to keep connection alive
   */
  startHeartbeat() {
    this.stopHeartbeat();
    
    this.heartbeatTimer = setInterval(() => {
      if (this.isConnected && this.ws) {
        try {
          // Send ping message
          this.send({
            action: 'ping',
            timestamp: new Date().toISOString()
          });
        } catch (error) {
          console.error('Error sending heartbeat:', error);
        }
      }
    }, this.heartbeatInterval);
  }

  /**
   * Stop heartbeat timer
   */
  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * Send data through WebSocket
   * @param {Object} data - Data to send
   */
  send(data) {
    if (!this.isConnected || !this.ws) {
      console.warn('WebSocket not connected, queueing message');
      this.pendingMessages.push(data);
      return;
    }

    try {
      this.ws.send(JSON.stringify(data));
    } catch (error) {
      console.error('Error sending WebSocket message:', error);
      this.pendingMessages.push(data);
      throw error;
    }
  }

  /**
   * Send pending messages after reconnection
   */
  sendPendingMessages() {
    if (this.pendingMessages.length === 0) {
      return;
    }

    console.log(`Sending ${this.pendingMessages.length} pending messages`);

    const messages = [...this.pendingMessages];
    this.pendingMessages = [];

    messages.forEach(message => {
      try {
        this.send(message);
      } catch (error) {
        console.error('Error sending pending message:', error);
      }
    });
  }

  /**
   * Send a message through WebSocket
   * @param {string} threadId - Thread ID
   * @param {string} content - Message content
   * @param {Object} metadata - Additional metadata (senderName, senderPhotoUrl)
   */
  sendMessage(threadId, content, metadata = {}) {
    this.send({
      action: 'sendMessage',
      threadId,
      content,
      senderName: metadata.senderName,
      senderPhotoUrl: metadata.senderPhotoUrl
    });
  }

  /**
   * Send typing indicator
   * @param {string} threadId - Thread ID
   * @param {boolean} isTyping - Whether user is typing
   */
  sendTypingIndicator(threadId, isTyping) {
    this.send({
      action: 'typing',
      threadId,
      isTyping
    });
  }

  /**
   * Update message status
   * @param {string} messageId - Message ID
   * @param {string} status - Status (delivered, read)
   */
  updateMessageStatus(messageId, status) {
    this.send({
      action: 'updateStatus',
      messageId,
      status
    });
  }

  // Event listener management

  /**
   * Add message listener
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  onMessage(callback) {
    this.messageListeners.push(callback);
    return () => {
      this.messageListeners = this.messageListeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Add typing indicator listener
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  onTyping(callback) {
    this.typingListeners.push(callback);
    return () => {
      this.typingListeners = this.typingListeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Add presence update listener
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  onPresence(callback) {
    this.presenceListeners.push(callback);
    return () => {
      this.presenceListeners = this.presenceListeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Add status update listener
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  onStatus(callback) {
    this.statusListeners.push(callback);
    return () => {
      this.statusListeners = this.statusListeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Add connection state listener
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  onConnectionChange(callback) {
    this.connectionListeners.push(callback);
    return () => {
      this.connectionListeners = this.connectionListeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Add error listener
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  onError(callback) {
    this.errorListeners.push(callback);
    return () => {
      this.errorListeners = this.errorListeners.filter(cb => cb !== callback);
    };
  }

  // Notification methods

  notifyMessageListeners(message) {
    this.messageListeners.forEach(callback => {
      try {
        callback(message);
      } catch (error) {
        console.error('Error in message listener:', error);
      }
    });
  }

  notifyTypingListeners(data) {
    this.typingListeners.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error('Error in typing listener:', error);
      }
    });
  }

  notifyPresenceListeners(data) {
    this.presenceListeners.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error('Error in presence listener:', error);
      }
    });
  }

  notifyStatusListeners(data) {
    this.statusListeners.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error('Error in status listener:', error);
      }
    });
  }

  notifyConnectionChange(isConnected) {
    this.connectionListeners.forEach(callback => {
      try {
        callback(isConnected);
      } catch (error) {
        console.error('Error in connection listener:', error);
      }
    });
  }

  notifyError(error) {
    this.errorListeners.forEach(callback => {
      try {
        callback(error);
      } catch (error) {
        console.error('Error in error listener:', error);
      }
    });
  }

  /**
   * Get connection status
   * @returns {boolean} True if connected
   */
  getConnectionStatus() {
    return this.isConnected;
  }
}

// Create singleton instance
const websocketService = new WebSocketService();

export default websocketService;
